import * as t from 'utils/comparatorTypes';
import * as f from 'utils/fieldTypes';

export type ComparatorOption = {
  id?: string;
  value?: string;
  text: string;
  description?: string;
  multiText?: string;
};
/**
 * makeOption
 * Creates a dropdown option for a comparator type.
 *
 * ```
 * makeOption(value, text, description, multi)
 * ```
 * @param { String } value: The value of the comparator.
 * @param { String } text: The display text.
 * @param { String } description: Description / Symbol of the comparator.
 * @param { String } multi: Text to display when multi-valued.
 * @returns { Object } The dropdown option for the comparator.
 */
export const makeOption = (
  value: string,
  text: string,
  description?: string,
  multi?: string,
  id?: string,
) => ({
  id,
  value,
  text,
  description,
  multiText: multi || text,
});

/**
 * aliasOption
 * Creates an alias for a comparator dropdown option.
 *
 * ```
 * makeOption(value, text, description, multi)
 * ```
 * @param { Object } option: The comparator option to alias.
 * @param { Object } updatedOptions: Option to override the given option;
 * @returns { Object } The alias dropdown option for the comparator.
 */
export const aliasOption = (
  option: ComparatorOption,
  updatedOptions: Partial<ComparatorOption>,
) => ({
  ...option,
  ...updatedOptions,
});

/*------------------------------------------------------------------------------------------------*/
/* Default Comparators                                                                            */
/*------------------------------------------------------------------------------------------------*/
const CONTAINS = makeOption(t.CONTAINS, 'Contains', '⊃');
const DOES_NOT_EQUAL = makeOption(t.DOES_NOT_EQUAL, 'Does Not Equal', '≠', 'Equals Anything But');
const EQUALS = makeOption(t.EQUALS, 'Equals', '=');
const END_RANGE = makeOption(t.END_RANGE, 'End Range', '⇥');
const GREATER_THAN = makeOption(t.GREATER_THAN, 'Greater Than', '>');
const GREATER_THAN_OR_EQUAL = makeOption(t.GREATER_THAN_OR_EQUAL, 'Greater Than Or Equal', '>=');
const IS_BETWEEN = makeOption(t.IS_BETWEEN, 'Is Between', '↔');
const IS_NOT_SET = makeOption(t.IS_NOT_SET, 'Is Not Set', '∄');
const IS_SET = makeOption(t.IS_SET, 'Is Set', '∃');
const IS_WITHIN = makeOption(t.WITHIN, 'Is Within', '↔');
const LESS_THAN = makeOption(t.LESS_THAN, 'Less Than', '<');
const LESS_THAN_OR_EQUAL = makeOption(t.LESS_THAN_OR_EQUAL, 'Less Than Or Equal', '<=');
const MATCHES_REGEX = makeOption(t.MATCHES_REGEX, 'Matches Regex', '.*');
const STARTS_WITH = makeOption(t.STARTS_WITH, 'Starts With', '⊳');
const START_RANGE = makeOption(t.START_RANGE, 'Start Range', '⇤');

/*------------------------------------------------------------------------------------------------*/
/* Alias Comparators                                                                              */
/*------------------------------------------------------------------------------------------------*/
const INCLUDES = aliasOption(EQUALS, { text: 'Includes' });
const NOT_INCLUDES = aliasOption(DOES_NOT_EQUAL, {
  text: 'Does Not Include',
  multiText: 'Includes Anything But',
});
const IS_AFTER = makeOption(t.GREATER_THAN, 'Is After', '⤻');
const IS_ON_OR_AFTER = makeOption(t.GREATER_THAN_OR_EQUAL, 'Is on or After', '⤻=');
const IS_BEFORE = makeOption(t.LESS_THAN, 'Is Before', '⤺');
const IS_ON_OR_BEFORE = makeOption(t.LESS_THAN_OR_EQUAL, 'Is on or Before', '⤺=');
const IS_EXACTLY = aliasOption(EQUALS, { text: 'Is Exactly' });
const IS_ANY_TIME_EXCEPT = aliasOption(DOES_NOT_EQUAL, { text: 'Is Any Time Except' });
const IS_ONE_OF = aliasOption(EQUALS, {
  id: t.IS_ONE_OF,
  text: 'Is One Of',
  description: undefined,
});
const IS_NOT_ONE_OF = aliasOption(DOES_NOT_EQUAL, {
  id: t.IS_NOT_ONE_OF,
  text: 'Is Not One Of',
  description: undefined,
});

/*------------------------------------------------------------------------------------------------*/
/* Comparator Type Lists                                                                          */
/*------------------------------------------------------------------------------------------------*/
export const booleanTypeComparators = [EQUALS, DOES_NOT_EQUAL];

export const campaignLabelsComparators = [EQUALS];

export const defaultComparators = [
  DOES_NOT_EQUAL,
  EQUALS,
  GREATER_THAN,
  GREATER_THAN_OR_EQUAL,
  IS_NOT_SET,
  IS_SET,
  LESS_THAN,
  LESS_THAN_OR_EQUAL,
  STARTS_WITH,
];

export const dateTypeComparators = [
  IS_AFTER,
  IS_ON_OR_AFTER,
  IS_BEFORE,
  IS_ON_OR_BEFORE,
  IS_EXACTLY,
  IS_ANY_TIME_EXCEPT,
  IS_SET,
];

export const dateTypeSegmentComparators = [...dateTypeComparators, IS_BETWEEN];

export const geoLocationTypeComparators = [IS_WITHIN, IS_SET, IS_NOT_SET];

export const listTypeComparators = [INCLUDES, NOT_INCLUDES];

export const numericTypeComparators = [
  GREATER_THAN,
  GREATER_THAN_OR_EQUAL,
  LESS_THAN,
  LESS_THAN_OR_EQUAL,
  EQUALS,
  DOES_NOT_EQUAL,
  IS_SET,
];

export const objectTypeComparators = [IS_SET];

export const stringTypeComparators = [EQUALS, DOES_NOT_EQUAL, IS_SET, STARTS_WITH];

export const stringTypeCatalogQueryComparators = [EQUALS, DOES_NOT_EQUAL, IS_SET];

export const regexTypeComparators = [MATCHES_REGEX, CONTAINS];

export const brandAffinityComparators = booleanTypeComparators;

export const multiSelectInputComparators = [IS_ONE_OF, IS_NOT_ONE_OF];

const comparators = {
  DOES_NOT_EQUAL,
  EQUALS,
  END_RANGE,
  GREATER_THAN,
  GREATER_THAN_OR_EQUAL,
  IS_BETWEEN,
  IS_NOT_SET,
  IS_SET,
  LESS_THAN,
  LESS_THAN_OR_EQUAL,
  START_RANGE,
  INCLUDES,
  NOT_INCLUDES,
  IS_AFTER,
  IS_ON_OR_AFTER,
  IS_BEFORE,
  IS_ON_OR_BEFORE,
  IS_EXACTLY,
  IS_ANY_TIME_EXCEPT,
};

export const symbolsNotShownOnView = ['⊃', '⇥', '↮', '⇹', '↔', '.*', '⊳', '⇤', '∃', '∄'];

/**
 * getDefaultComparator
 * Helper function to get the default comparator value for a field type.
 * @param { String } fieldType: The field type to get default comparator of.
 * @return { String } The value of the default comparator.
 */
export const getDefaultComparator = (fieldType: string) => {
  const defaultValues = {
    [f.BOOLEAN]: EQUALS,
    [f.DATE]: IS_EXACTLY,
    [f.DOUBLE]: EQUALS,
    [f.GEO_LOCATION]: IS_WITHIN,
    [f.LONG]: EQUALS,
    [f.OBJECT]: IS_SET,
    [f.STRING]: EQUALS,
  } as Record<string, ComparatorOption>;
  const { value } = defaultValues[fieldType] || EQUALS;
  return value;
};

export default comparators;
